import React from "react";
import { useTranslation } from "react-i18next";

const CalendarGrid = ({ 
  currentYear, 
  currentMonth, 
  selectedDate, 
  setSelectedDate, 
  onDateClick,
  markedDates = {},
  appointments
}) => {
  const { t } = useTranslation();
  const today = new Date();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const startDay = new Date(currentYear, currentMonth, 1).getDay();
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  // Bir günün detay bilgilerini göstermek için helper fonksiyon
  const renderDayMarkers = (day) => {
    if (!markedDates[day]) {
      return null;
    }

    // Kategorize edilmiş marker sayaçları
    let bookedCount = 0;
    let availableCount = 0;
    let userAppointmentCount = 0;

    // Markerları kategorize et
    markedDates[day].forEach(marker => {
      if (marker.isAvailable) {
        availableCount++;
      } else if (marker.isBooked) {
        bookedCount++;
      } else {
        userAppointmentCount++;
      }
    });

    // Tooltip içeriği oluştur - daha detaylı bilgi için
    const tooltipContent = markedDates[day].map(marker => {
      if (marker.isAvailable) {
        return `Available: ${marker.time}`;
      } else if (marker.isBooked) {
        return `Booked: ${marker.time}`;
      } else {
        return `Appointment: ${marker.time}`;
      }
    }).join(', ');

    return (
      <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1 items-center" title={tooltipContent}>
        {availableCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-background rounded-full"></div>
            <div className="text-[8px] text-text leading-tight">{availableCount}</div>
          </div>
        )}
        
        {bookedCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <div className="text-[8px] text-red-700 leading-tight">{bookedCount}</div>
          </div>
        )}
        
        {userAppointmentCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <div className="text-[8px] text-blue-700 leading-tight">{userAppointmentCount}</div>
          </div>
        )}
      </div>
    );
  };

  // Gün arka plan rengini belirleme
  const getDayBackgroundColor = (day) => {
    if (day === selectedDate) {
      return "bg-primary text-white";
    }
    
    if (day === todayDate && currentMonth === todayMonth && currentYear === todayYear) {
      return "bg-background";
    }

    if (markedDates[day]) {
      // Günlerin renklerini markerlarına göre belirle
      const hasBookedSlot = markedDates[day].some(marker => marker.isBooked);
      const hasAvailableSlot = markedDates[day].some(marker => marker.isAvailable);
      const hasUserAppointment = markedDates[day].some(marker => !marker.isBooked && !marker.isAvailable);
      
      if (hasUserAppointment) {
        return "bg-blue-50";  // Kullanıcının kendi randevusu için hafif mavi
      } else if (hasAvailableSlot && !hasBookedSlot) {
        return "bg-green-50"; // Sadece müsait slot varsa hafif yeşil
      } else if (hasBookedSlot && hasAvailableSlot) {
        return "bg-yellow-50"; // Hem dolu hem boş slot varsa hafif sarı
      } else if (hasBookedSlot) {
        return "bg-red-50";   // Sadece dolu slot varsa hafif kırmızı
      }
    }
    
    return "";
  };

  // Gün üzerine tıklandığında
  const handleDayClick = (day) => {
    setSelectedDate(day);
    onDateClick(day);
  };

  return (
    <div className="grid grid-cols-7 gap-0 text-center font-medium border-t border-l flex-grow">
      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
        <div key={day} className="py-3 border-b border-r font-semibold bg-gray-50">
          {t(day)}
        </div>
      ))}

      {/* Önceki ayın günleri */}
      {Array.from({ length: startDay }, (_, i) => prevMonthDays - startDay + i + 1).map((day) => (
        <div key={`prev-${day}`} className="py-3 border-b border-r text-gray-400 bg-gray-50">
          {day}
        </div>
      ))}

      {/* Mevcut ayın günleri */}
      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
        <button
          key={day}
          className={`
            py-3 border-b border-r text-lg font-semibold w-full transition relative
            ${getDayBackgroundColor(day)}
            hover:bg-gray-100 hover:opacity-90
          `}
          onClick={() => handleDayClick(day)}
        >
          {day}
          {renderDayMarkers(day)}
        </button>
      ))}

      {/* Sonraki ayın günleri */}
      {Array.from({ length: 42 - (startDay + daysInMonth) }, (_, i) => i + 1).map((day) => (
        <div key={`next-${day}`} className="py-3 border-b border-r text-gray-400 bg-gray-50">
          {day}
        </div>
      ))}
    </div>
  );
};

export default CalendarGrid;